import dayjs from 'dayjs';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { useTenant } from '../../app/context/tenant-context';
import { useUserId } from '../../app/hooks/accounts';
import { useEnsembleModelsQuery } from '../../app/hooks/tags';
import { getView } from '../../app/services/tenants';
// TODO: Uncomment when implementing API calls
// import {
//   useDeleteCustomView,
//   useEditCustomView,
//   useSaveCustomView,
// } from '../../app/hooks/accounts';
import {
  clearTenantViewStates,
  removeAllViewStatesExcept,
  removeViewState,
  restoreViewState,
  storeViewState,
} from '../utils/storage';
import { getTabDisplayName } from '../utils/tab-display';
import {
  saveTabsMetadata,
  loadTabsMetadata,
  saveActiveTabId,
  loadActiveTabId,
  saveActiveCollectionId,
  loadActiveCollectionId,
  clearTabMetadata,
} from '../utils/tab-metadata-storage';
import { chartHeightIncrements, transformToViewState } from '../utils/view-state-helpers';

import {
  AnomalySort,
  DataModeType,
  Ensemble,
  ModelStatus,
  ModelType,
  PersistCollection,
  PersistView,
  TimeRanges,
  ViewType,
  ViewState,
  TabbedView,
  PersistTimeSelection,
  ViewConfig,
  TrendSearchData,
} from '@controlrooms/models';

/**
 * Tab types
 */
export type TabType = 'ALERT_BOARD' | 'VIEWS' | 'ALERT_MANAGER' | 'LIMIT_MANAGER' | 'MANAGE';

/**
 * Sub-page types (for MANAGE tabs only)
 */
export type SubPage =
  | 'monitors'
  | 'formulas'
  | 'inheritance'
  | 'alerts'
  | 'user_groups'
  | 'webhooks';

/**
 * ViewCollection interface
 */
export interface ViewCollection {
  name: string;
  description?: string;
  viewsIds: string[]; // Array of view IDs in the collection
  views?: PersistView[]; // Optional array of PersistView objects
  collectionId?: string; // The collection ID from backend
  isDirty?: boolean; // Whether the collection has unsaved changes
  createdBy?: string; // Optional creator info
}

/**
 * Tab interface
 */
export interface Tab {
  id: string;
  type: TabType;
  name: string;
  description?: string; // Tab description (synced with ViewState)
  isDirty?: boolean; // Synced with ViewState.isDirty for dirty indicator and Save changes menu
  saved: boolean;
  viewId?: string; // For saved tabs
  viewType?: ViewType; // For VIEWS tabs (monitor | analyze | investigate)
  subPage?: SubPage; // For MANAGE tabs (monitors | formulas | inheritance | alerts | user_groups)
  isTrendSearch?: boolean; // For VIEWS+Analyze: sync from viewState.view[ANALYZE].isTrendSearch for derived tab label
  createdAt: number;
  lastAccessed: number;
  userId?: number; // For saved tabs - owner user ID
}

/**
 * Tab creation options
 */
export interface CreateTabOptions {
  type: TabType;
  viewType?: ViewType; // Required for VIEWS type (monitor | analyze | investigate)
  subPage?: SubPage; // For MANAGE type (monitors | formulas | inheritance | alerts | user_groups)
  viewId?: string; // For opening saved views
  name?: string; // Optional name override
}

/**
 * TabManagerContext interface
 */
interface TabManagerContextType {
  tabs: Tab[]; // Access all open tab metadata
  activeTabId: string | null; // Return the active tab id
  activeViewState: ViewState; // Synchronous access to active tab's viewState (always returns ViewState for active tab)
  getViewState: (id: string) => Promise<ViewState>; // Return the viewState of the provided tab-id
  createTab: (options: CreateTabOptions) => Promise<string>; // Create a new tab with specified options
  addNewTab: () => Promise<string>; // Create a new default VIEWS tab with Monitor mode
  openInNewTab: (viewState: ViewState) => Promise<string>; // Create a fresh new tab with meta and a viewState
  duplicateTab: (tabId: string) => Promise<string>; // Creates a new tab with a duplicate of the current viewState of the provided tabid
  switchTab: (tabId: string) => void; // Set the active tab id to the provided tabId
  openSavedView: (viewId: string) => Promise<string>; // Create a newTab with a viewState from the saved viewId (switches if already open)
  closeTab: (tabId: string) => void;
  closeAllTabs: () => void; // Close all open tabs at once
  closeOtherTabs: (keepTabId: string) => Promise<void>; // Close all tabs except this one (persist + state, no per-tab navigation)
  openTabInNewWindow: (tabId: string) => void; // Opens a fresh new browser session tab with the currentViewState of the tabId
  updateTab: (tabId: string) => Promise<void>; // Saves the currentViewState of the passed tabId to backend - applies only to saved view
  updateTabMetadata: (
    tabId: string,
    updates: Partial<Omit<Tab, 'id' | 'createdAt' | 'lastAccessed'>>,
  ) => void; // Update tab metadata (name, viewType, etc.)
  /** Replace unsaved tab id with saved viewId: migrate persistence, update tab id, keep id === viewId for saved tabs */
  replaceTabIdWithViewId: (
    oldTabId: string,
    viewId: string,
    metadataUpdates: Partial<Omit<Tab, 'id' | 'createdAt' | 'lastAccessed'>>,
    updatedViewState: ViewState,
  ) => Promise<void>;
  persistTabState: (tabId: string, viewState: ViewState) => Promise<void>;
  updateActiveTabViewState: (viewState: ViewState) => Promise<void>; // Update active tab's ViewState (does not set isDirty; use makeDirty() when needed)
  makeDirty: () => Promise<void>; // Mark the active tab's viewState as dirty
  deleteTab: (viewId: string) => Promise<void>; // Calls the delete endpoint passing the viewId - applies only to saved view
  revertTab: (tabId: string) => Promise<void>; // Fetches the saved view passing the tab-id and resets the currentState
  handleTenantSwitch: () => void;
  switchTabType: (options: CreateTabOptions) => Promise<void>; // Switch active tab's type (preserves state, makes dirty if saved)
  clearTrendSearchData: () => Promise<void>; // Clear trend search data from ANALYZE view
  setTrendSearchData: (trendSearchData: TrendSearchData, isDirty?: boolean) => Promise<void>; // Set trend search data in ANALYZE view
  viewCollection: ViewCollection; // Current collection state
  setViewCollection: (
    updates: Partial<ViewCollection> | ((prev: ViewCollection) => ViewCollection),
  ) => void; // Update collection state
  loadCollection: (collection: PersistCollection) => Promise<void>; // Load a collection (close all tabs, open views, set collection state)
  closeCollection: () => void; // Close the current collection (clear collection state and activeCollectionId)
  activeCollectionId: string | null; // Currently active collection ID
  getSavedViewIds: () => string[]; // Get saved view IDs from current tabs (for SaveCollectionModal)
  getQueryKeyWithViewId: (label: string) => string; // Generate query key with active tab ID for cache isolation
  recordTimelineHistory: () => Promise<void>; // Record current timeSelection to history before making changes (for undo functionality)
  handleUndoTimeSelection: () => Promise<void>; // Undo timeSelection to previous state from history
  searchMode: boolean; // Flag to enable SearchMode on Assess screen
  setSearchMode: (value: boolean | ((prev: boolean) => boolean)) => void; // Update searchMode flag
  handleIncrementAssessChartHeight: () => Promise<void>; // Increase chart height for Assess view
  handleDecrementAssessChartHeight: () => Promise<void>; // Decrease chart height for Assess view
}

const TabManagerContext = createContext<TabManagerContextType | undefined>(undefined);

/**
 * Lightweight context for tab bar selection only. Updated optimistically on click
 * so the tab bar highlights immediately without waiting for the full tree commit.
 * Do not add this to TabManagerContext value so heavy consumers don't re-render.
 */
const TabDisplayContext = createContext<string | null>(null);

// Storage keys function removed - now using tab-metadata-storage functions

/**
 * TabManagerProvider component
 */
export const TabManagerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { tenant } = useTenant();
  const userId = useUserId();
  const navigate = useNavigate();
  const { ensembleFamilies = [] } = useEnsembleModelsQuery();

  // Track previous tenant to detect changes
  // Initialize to undefined so first render doesn't trigger reload
  const previousTenantRef = useRef<number | undefined>(undefined);

  // ============================================================================
  // STATE — Tab metadata, active tab, view state (per tab), collection
  // ============================================================================

  // Tab metadata: list of tabs (id, name, viewType, type, etc.)
  const [tabs, setTabs] = useState<Tab[]>(() => {
    try {
      return loadTabsMetadata(tenant);
    } catch {
      return [];
    }
  });

  // Initialize activeTabId from sessionStorage using new storage functions
  const [activeTabId, setActiveTabId] = useState<string | null>(() => {
    try {
      return loadActiveTabId(tenant);
    } catch {
      return null;
    }
  });

  // Optimistic tab bar selection: updated immediately on click so tab bar doesn't wait for heavy commit.
  // Synced from activeTabId in effect; not part of main context value so only tab bar re-renders when this changes.
  const [displayActiveTabId, setDisplayActiveTabId] = useState<string | null>(() => {
    try {
      return loadActiveTabId(tenant);
    } catch {
      return null;
    }
  });

  // Initialize activeCollectionId from sessionStorage
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(() => {
    try {
      return loadActiveCollectionId(tenant);
    } catch {
      return null;
    }
  });

  // Helper for default ViewState (used by view-state and persistence logic)
  const getDefaultViewState = useCallback((): ViewState => {
    const now = dayjs();
    const defaultTimeSelection = {
      startTime: now.subtract(12, 'hour'),
      endTime: now,
      timezone: dayjs.tz.guess() || 'UTC',
      timePreset: 'last12hours' as const,
      timeRange: TimeRanges.PRESET,
      streamingTimeInSeconds: 43200, // 12 hours
      nowSelected: false,
    };

    return {
      viewId: 'default',
      version: 1,
      userId: userId,
      name: '',
      description: '',
      isDefault: false,
      selectedMode: ViewType.MONITOR,
      tenant_id: tenant,
      timeSelection: defaultTimeSelection,
      timeSelectionHistory: [],
      view_id: 'default',
      view: {
        [ViewType.MONITOR]: {
          severityFilter: 0,
          showLimits: true,
          selectedFolders: [],
          selectedTags: [],
        },
        [ViewType.ANALYZE]: {
          selectedTags: [],
          showLimits: true,
          showLabeledEvents: true,
          pinnedTags: [],
          hiddenTags: [],
          selectedFolders: [],
          isTrendSearch: false,
          selectedModelPreview: null,
        },
        [ViewType.INVESTIGATE]: {
          selectedFolders: [],
          selectedTags: {},
          showAnomalies: true,
          organize: 'overlays' as const,
          groupByUom: false,
          showLimits: true,
          highlightedTag: null,
          lockedTag: null,
        },
        [ViewType.ALERT_BOARD]: {
          selected_board: undefined,
          selected_view: 'grid',
          time_selection: undefined,
          filters: {},
          selectedAlert: null,
          preview: { timeSelection: defaultTimeSelection, timeSelectionHistory: [] },
        },
        [ViewType.ALERT_MANAGER]: {
          folder: undefined,
          tag: undefined,
          rule_id: undefined,
        },
        [ViewType.LIMIT_MANAGER]: {
          folder: undefined,
          tag: undefined,
          limit_id: undefined,
        },
        [ViewType.MANAGE_MONITOR]: {
          folder: undefined,
          tag: undefined,
          ensemble_id: undefined,
          modelDataFilters: {
            dataType: ['Models'],
            modelType: ModelType.UNIVARIATE_ENSEMBLE,
            modelStatus: ModelStatus.DEPLOYMENT_COMPLETE,
          },
        },
        [ViewType.MANAGE_FORMULAS]: {
          folder: undefined,
          tag: undefined,
          ensemble_id: undefined,
        },
        [ViewType.MANAGE_INHERITANCE]: {
          folder: undefined,
          tag: undefined,
          ensemble_id: undefined,
        },
        [ViewType.MANAGE_ALERTS]: {
          folder: undefined,
          tag: undefined,
        },
        [ViewType.MANAGE_USER_GROUPS]: {
          user_group_id: undefined,
        },
        [ViewType.MANAGE_WEBHOOKS]: {
          webhook_id: undefined,
        },
      },
      config: {
        [ViewType.ANALYZE]: {
          chartHeight: chartHeightIncrements[0],
          limitFilters: {
            HH: true,
            H: true,
            L: true,
            LL: true,
          },
          sortOption: AnomalySort.SEVERITY,
        },
      },
      selectedEnsemble:
        ensembleFamilies?.find(
          (ef: Ensemble) => (ef as Ensemble & { main_family?: boolean }).main_family,
        ) ||
        ensembleFamilies?.[0] ||
        null,
      shared: false,
      isDirty: false,
      isHidden: false,
      isDeleted: false,
      dataModeType: DataModeType.AUTO,
    };
  }, [userId, tenant, ensembleFamilies]);

  // Per-tab view state: active tab's ViewState (config, time selection, view[MONITOR/ANALYZE], etc.)
  const [activeViewState, setActiveViewState] = useState<ViewState>(getDefaultViewState);

  // Collection state (name, viewsIds, views, isDirty)
  const [viewCollection, setViewCollectionState] = useState<ViewCollection>(() => {
    try {
      const stored = sessionStorage.getItem(`viewCollection_${tenant}`);
      return stored
        ? JSON.parse(stored)
        : {
            name: '',
            description: '',
            viewsIds: [],
            views: [],
            isDirty: false,
          };
    } catch {
      return {
        name: '',
        description: '',
        viewsIds: [],
        views: [],
        isDirty: false,
      };
    }
  });

  // ============================================================================
  // PERSISTENCE — Tab metadata (load on tenant change, save tabs, save activeTabId + URL)
  // ============================================================================

  // Load tab metadata (and collection) from storage when tenant changes
  useEffect(() => {
    const previousTenant = previousTenantRef.current;
    // If tenant changed, reload tabs from new tenant's sessionStorage
    if (previousTenant !== undefined && previousTenant !== tenant) {
      // Clear tabs first, then reload from new tenant's sessionStorage
      setTabs([]);
      setActiveTabId(null);
      setActiveCollectionId(null);
      // Set default ViewState when tenant changes
      setActiveViewState(getDefaultViewState());
      // Clear viewCollection for new tenant
      setViewCollectionState({
        name: '',
        description: '',
        viewsIds: [],
        views: [],
        isDirty: false,
      });

      try {
        const newTabs = loadTabsMetadata(tenant);
        setTabs(newTabs);

        const storedActiveTab = loadActiveTabId(tenant);
        setActiveTabId(storedActiveTab);

        const storedActiveCollection = loadActiveCollectionId(tenant);
        setActiveCollectionId(storedActiveCollection);

        // Load viewCollection for new tenant
        try {
          const stored = sessionStorage.getItem(`viewCollection_${tenant}`);
          if (stored) {
            setViewCollectionState(JSON.parse(stored));
          }
        } catch {
          // Use default
        }
      } catch {
        // Already cleared above, so this is just a safety net
        setTabs([]);
        setActiveTabId(null);
        setActiveCollectionId(null);
        // Set default ViewState when clearing tabs
        setActiveViewState(getDefaultViewState());
        setViewCollectionState({
          name: '',
          description: '',
          viewsIds: [],
          views: [],
          isDirty: false,
        });
      }
    }
    // Update ref for next render
    previousTenantRef.current = tenant;
  }, [tenant, getDefaultViewState]);

  // Persist tab metadata to sessionStorage (tabs array → tabs-${tenant})
  useEffect(() => {
    try {
      saveTabsMetadata(tenant, tabs);
    } catch (error) {
      console.error('Failed to persist tabs:', error);
    }
  }, [tabs, tenant]);

  // Sync display tab id when activeTabId changes (tab bar highlight)
  useEffect(() => {
    setDisplayActiveTabId(activeTabId);
  }, [activeTabId]);

  // Persist activeTabId to sessionStorage and sync URL
  useEffect(() => {
    try {
      if (activeTabId) {
        // Verify the activeTabId still exists in tabs array
        const activeTab = tabs.find((t) => t.id === activeTabId);
        if (activeTab) {
          saveActiveTabId(tenant, activeTabId);

          // Sync URL with active tab (only if URL doesn't match)
          const viewId = activeTab.viewId || activeTab.id;
          const newPath = `/app/t/${tenant}/${viewId}`;

          // Only update URL if it's different to avoid unnecessary navigation
          if (window.location.pathname !== newPath) {
            navigate(newPath, { replace: true });
          }
        } else {
          // activeTabId points to a non-existent tab, clear it
          // This can happen when a tab is closed but activeTabId wasn't updated
          if (tabs.length > 0) {
            // Restore to first tab
            const firstTab = tabs[0];
            setActiveTabId(firstTab.id);
            const viewId = firstTab.viewId || firstTab.id;
            navigate(`/app/t/${tenant}/${viewId}`, { replace: true });
          } else {
            setActiveTabId(null);
            // Set default ViewState when no tabs exist
            setActiveViewState(getDefaultViewState());
            saveActiveTabId(tenant, null);
            navigate(`/app/t/${tenant}`, { replace: true });
          }
        }
      } else {
        saveActiveTabId(tenant, null);
        setActiveViewState(getDefaultViewState());
        // Only navigate to base tenant path if we're not already there
        const basePath = `/app/t/${tenant}`;
        if (
          window.location.pathname !== basePath &&
          !window.location.pathname.startsWith(`${basePath}/`)
        ) {
          navigate(basePath, { replace: true });
        }
      }
    } catch (error) {
      console.error('Failed to persist activeTabId:', error);
    }
  }, [activeTabId, tabs, tenant, navigate, getDefaultViewState]);

  // ============================================================================
  // PERSISTENCE — Collections (viewCollection, activeCollectionId)
  // ============================================================================

  useEffect(() => {
    try {
      sessionStorage.setItem(`viewCollection_${tenant}`, JSON.stringify(viewCollection));
    } catch (error) {
      console.error('Failed to persist viewCollection:', error);
    }
  }, [viewCollection, tenant]);

  useEffect(() => {
    try {
      saveActiveCollectionId(tenant, activeCollectionId);
    } catch (error) {
      console.error('Failed to persist activeCollectionId:', error);
    }
  }, [activeCollectionId, tenant]);

  // View state is persisted in callbacks via storeViewState (updateActiveTabViewState,
  // persistTabState, createTabInternal, replaceTabIdWithViewId, load activeViewState effect, etc.).
  // No dedicated "persist view state" effect — each callback persists when it mutates view state.

  // ============================================================================
  // TAB — Tab metadata (switch, update metadata, replace id, close, open in new window)
  // ============================================================================

  /**
   * Switch active tab. Updates display (tab bar) immediately so selection doesn't lag;
   * defers full activeTabId update so heavy content re-render doesn't block tab bar paint.
   */
  const switchTab = useCallback((tabId: string) => {
    setDisplayActiveTabId(tabId);
    setTimeout(() => setActiveTabId(tabId), 0);
  }, []);

  // ============================================================================
  // PER-TAB — View state (defaults, get/store, update active, create/duplicate/open/close tab)
  // ============================================================================

  /**
   * Create a default ViewState for a tab
   * Initializes default state for all view types to preserve state when switching
   */
  const createDefaultViewState = useCallback(
    (tab: Tab): ViewState => {
      const now = dayjs();
      const defaultTimeSelection = {
        startTime: now.subtract(12, 'hour'),
        endTime: now,
        timezone: dayjs.tz.guess() || 'UTC',
        timePreset: 'last12hours' as const,
        timeRange: TimeRanges.PRESET,
        streamingTimeInSeconds: 43200, // 12 hours
        nowSelected: false,
      };

      const defaultPersistTimeSelection =
        PersistTimeSelection.ofTimeSelection(defaultTimeSelection);

      const view: TabbedView = {
        // VIEWS tab types
        [ViewType.MONITOR]: {
          severityFilter: 0,
          showLimits: true,
          selectedFolders: [],
          selectedTags: [],
        },
        [ViewType.ANALYZE]: {
          selectedTags: [],
          showLimits: true,
          showLabeledEvents: true,
          pinnedTags: [],
          hiddenTags: [],
          selectedFolders: [],
          isTrendSearch: false,
          selectedModelPreview: null,
        },
        [ViewType.INVESTIGATE]: {
          selectedFolders: [],
          selectedTags: {},
          showAnomalies: true,
          organize: 'overlays' as const,
          groupByUom: false,
          showLimits: true,
          highlightedTag: null,
          lockedTag: null,
        },
        // Other tab types - initialize defaults
        [ViewType.ALERT_BOARD]: {
          selected_board: undefined,
          selected_view: 'grid',
          time_selection: defaultPersistTimeSelection,
          filters: {},
          selectedAlert: null,
          preview: { timeSelection: defaultTimeSelection, timeSelectionHistory: [] },
        },
        [ViewType.ALERT_MANAGER]: {
          folder: undefined,
          tag: undefined,
          rule_id: undefined,
        },
        [ViewType.LIMIT_MANAGER]: {
          folder: undefined,
          tag: undefined,
          limit_id: undefined,
        },
        // MANAGE sub-page types - initialize defaults
        [ViewType.MANAGE_MONITOR]: {
          folder: undefined,
          tag: undefined,
          ensemble_id: undefined,
          modelDataFilters: {
            dataType: ['Models'],
            modelType: ModelType.UNIVARIATE_ENSEMBLE,
            modelStatus: ModelStatus.DEPLOYMENT_COMPLETE,
          },
        },
        [ViewType.MANAGE_FORMULAS]: {
          folder: undefined,
          tag: undefined,
          ensemble_id: undefined,
        },
        [ViewType.MANAGE_INHERITANCE]: {
          folder: undefined,
          tag: undefined,
          ensemble_id: undefined,
        },
        [ViewType.MANAGE_ALERTS]: {
          folder: undefined,
          tag: undefined,
        },
        [ViewType.MANAGE_USER_GROUPS]: {
          user_group_id: undefined,
        },
        [ViewType.MANAGE_WEBHOOKS]: {
          webhook_id: undefined,
        },
      };

      // Config is only for VIEWS tabs
      const config: ViewConfig = {
        [ViewType.ANALYZE]: {
          chartHeight: chartHeightIncrements[0],
          limitFilters: {
            HH: true,
            H: true,
            L: true,
            LL: true,
          },
          sortOption: AnomalySort.SEVERITY,
        },
      };

      const defaultEnsemble =
        ensembleFamilies?.find(
          (ef: Ensemble) => (ef as Ensemble & { main_family?: boolean }).main_family,
        ) ||
        ensembleFamilies?.[0] ||
        null;

      return {
        viewId: tab.id,
        version: 1,
        userId: userId,
        name: tab.name,
        description: '',
        isDefault: false,
        selectedMode: tab.viewType || ViewType.MONITOR,
        tenant_id: tenant,
        timeSelection: defaultTimeSelection,
        timeSelectionHistory: [],
        view_id: tab.id,
        view,
        config,
        selectedEnsemble: defaultEnsemble,
        shared: false,
        isDirty: false,
        isHidden: false,
        isDeleted: false,
        dataModeType: DataModeType.AUTO,
        tabType: tab.type,
        subPage: tab.subPage,
      };
    },
    [userId, tenant, ensembleFamilies],
  );

  /**
   * Internal helper to create a new tab
   * For saved views (viewId provided), checks if already open and switches to it
   * For unsaved tabs, creates a new tab with default ViewState
   * Auto-creates and stores ViewState on tab creation
   */
  const createTabInternal = useCallback(
    async (options: CreateTabOptions): Promise<string> => {
      // If opening a saved view (viewId provided), check if it's already open
      if (options.viewId) {
        const existingTab = tabs.find((t) => t.viewId === options.viewId);
        if (existingTab) {
          // Tab with this viewId already exists, switch to it instead of creating new
          setActiveTabId(existingTab.id);
          return existingTab.id;
        }
      }

      // Create new tab (either unsaved tab or saved view that's not already open)
      const tabId = uuidv4();
      const now = Date.now();

      const tabName: string = options.name ?? '';

      const newTab: Tab = {
        id: tabId,
        type: options.type,
        name: tabName,
        saved: options.viewId ? true : false, // Mark as saved if viewId provided
        viewId: options.viewId,
        viewType: options.viewType,
        subPage: options.subPage,
        createdAt: now,
        lastAccessed: now,
        userId: options.viewId ? userId : undefined,
      };

      setTabs((prev) => [...prev, newTab]);
      setActiveTabId(tabId);

      // Auto-create and store ViewState for all tab types
      // For saved views, try to restore from storage first; if not found, fetch from backend
      // For unsaved tabs, always create default ViewState
      if (options.viewId) {
        // Try to restore saved view's ViewState from storage
        let savedViewState = await restoreViewState(tabId, options.viewId);

        // If not in storage, fetch from backend
        if (!savedViewState) {
          try {
            const persistView = await getView(options.viewId);
            const { transformToViewState } = await import('../utils/view-state-helpers');
            savedViewState = transformToViewState(
              persistView,
              null, // ensembleFamily - will be set later if needed
              tenant,
              options.viewId,
            );
          } catch (error) {
            console.error('Failed to fetch saved view:', error);
            // Fall through to create default ViewState
          }
        }

        if (savedViewState) {
          // ViewState exists (from storage or backend), use it
          const viewStateToStore = savedViewState;
          await storeViewState(tabId, viewStateToStore);

          // Update tab metadata from ViewState (name, description, tabType, isTrendSearch, etc.)
          const isTrendSearch =
            viewStateToStore.selectedMode === ViewType.ANALYZE
              ? Boolean(viewStateToStore.view[ViewType.ANALYZE]?.isTrendSearch)
              : undefined;

          setTabs((prev) =>
            prev.map((t) =>
              t.id === tabId
                ? {
                    ...t,
                    name: viewStateToStore.name ?? t.name,
                    description: viewStateToStore.description ?? t.description,
                    type: viewStateToStore.tabType ? (viewStateToStore.tabType as TabType) : t.type,
                    viewType: viewStateToStore.selectedMode ?? t.viewType,
                    subPage: viewStateToStore.subPage ?? t.subPage,
                    isTrendSearch,
                  }
                : t,
            ),
          );

          // Update activeViewState if this is the active tab
          if (tabId === activeTabId) {
            setActiveViewState(viewStateToStore);
          }
        } else {
          // Saved view but ViewState not found, create default
          const defaultViewState = createDefaultViewState(newTab);
          // Update viewId to match the saved view's viewId
          defaultViewState.viewId = options.viewId;
          defaultViewState.view_id = options.viewId;
          await storeViewState(tabId, defaultViewState);
          // Update activeViewState if this is the active tab
          if (tabId === activeTabId) {
            setActiveViewState(defaultViewState);
          }
        }
      } else {
        // Unsaved tab, create default ViewState for all tab types
        const defaultViewState = createDefaultViewState(newTab);
        await storeViewState(tabId, defaultViewState);
        // Update activeViewState if this is the active tab
        if (tabId === activeTabId) {
          setActiveViewState(defaultViewState);
        }
      }

      return tabId;
    },
    [userId, tabs, activeTabId, createDefaultViewState, tenant],
  );
  /**
   * Create a new tab with specified options
   */
  const createTab = useCallback(
    async (options: CreateTabOptions): Promise<string> => {
      return createTabInternal(options);
    },
    [createTabInternal],
  );

  /**
   * Add a new default VIEWS tab with Monitor mode
   */
  const addNewTab = useCallback(async (): Promise<string> => {
    return createTabInternal({
      type: 'VIEWS',
      viewType: ViewType.MONITOR,
    });
  }, [createTabInternal]);

  /**
   * Open saved view - creates new tab if not found, switches if already open
   * Fetches the view from backend to get tabType, then creates tab with correct type
   */
  const openSavedView = useCallback(
    async (viewId: string): Promise<string> => {
      // First try to find by viewId (saved views)
      let tab = tabs.find((t) => t.viewId === viewId);
      // If not found, try to find by id (unsaved views)
      if (!tab) {
        tab = tabs.find((t) => t.id === viewId);
      }

      if (tab) {
        // Tab already exists, switch to it
        switchTab(tab.id);
        return tab.id;
      }

      // Tab doesn't exist, fetch view from backend to get tabType
      let tabType: TabType = 'VIEWS';
      let viewType: ViewType | undefined = ViewType.MONITOR;
      let subPage: SubPage | undefined = undefined;
      let viewName: string | undefined = undefined;

      try {
        const persistView = await getView(viewId);
        tabType = persistView.view_type || 'VIEWS';
        if (persistView.view?.type) {
          viewType = persistView.view.type as ViewType;
        }
        subPage = persistView.sub_page;
        viewName = persistView.view_name;
      } catch (error) {
        console.error('Failed to fetch saved view, using defaults:', error);
        // Use defaults if fetch fails
      }

      // Create new tab with saved view, using tabType and name from backend
      return createTabInternal({
        type: tabType,
        viewId: viewId,
        viewType: viewType,
        subPage: subPage,
        name: viewName,
      });
    },
    [tabs, switchTab, createTabInternal],
  );

  /**
   * Duplicate a tab
   * Creates a new tab with copied ViewState. Duplicated saved views become unsaved (no viewId)
   * to prevent duplicate viewId conflicts.
   * If the original tab doesn't have a ViewState, creates a default one.
   */
  const duplicateTab = useCallback(
    async (tabId: string): Promise<string> => {
      const tab = tabs.find((t) => t.id === tabId);
      if (!tab) {
        throw new Error(`Tab ${tabId} not found`);
      }

      // Restore ViewState from original tab, or create default if it doesn't exist
      // Try viewId first (for saved views), then tabId (for unsaved tabs)
      const viewId = tab.viewId;
      let originalViewState = await restoreViewState(tabId, viewId);
      const defaultState = createDefaultViewState(tab);

      if (!originalViewState) {
        // Tab doesn't have ViewState yet, use default one
        originalViewState = defaultState;
      } else {
        // Ensure ViewState has all required fields (in case it's incomplete)
        // Check if timeSelection is valid (has startTime and endTime as dayjs objects)
        const hasValidTimeSelection =
          originalViewState.timeSelection &&
          typeof originalViewState.timeSelection === 'object' &&
          'startTime' in originalViewState.timeSelection &&
          'endTime' in originalViewState.timeSelection &&
          dayjs.isDayjs(originalViewState.timeSelection.startTime) &&
          dayjs.isDayjs(originalViewState.timeSelection.endTime);

        // Merge with defaults, ensuring all required fields are present
        originalViewState = {
          ...defaultState,
          ...originalViewState,
          // Preserve original name but ensure it exists
          name: originalViewState.name || defaultState.name,
          // Ensure timeSelection exists and is valid (must be dayjs objects)
          timeSelection: hasValidTimeSelection
            ? originalViewState.timeSelection
            : defaultState.timeSelection,
          // Ensure view exists (merge to preserve any custom settings)
          view: originalViewState.view
            ? {
                ...defaultState.view,
                ...originalViewState.view,
              }
            : defaultState.view,
          // Ensure config exists
          config: originalViewState.config || defaultState.config,
          // Ensure other required fields
          timeSelectionHistory: originalViewState.timeSelectionHistory || [],
          tenant_id: originalViewState.tenant_id || defaultState.tenant_id,
          version: originalViewState.version || defaultState.version,
          userId: originalViewState.userId || defaultState.userId,
        };
      }
      const newTabId = uuidv4();
      const now = Date.now();
      const baseName = originalViewState.name?.trim() || getTabDisplayName(tab);
      const copyName = `${baseName} (Copy)`;

      const duplicatedViewState: ViewState = {
        ...originalViewState,
        viewId: newTabId,
        name: copyName,
        view_id: newTabId,
        isDefault: false,
      };

      await storeViewState(newTabId, duplicatedViewState);

      const newTab: Tab = {
        id: newTabId,
        type: tab.type,
        name: copyName,
        saved: false,
        viewId: undefined,
        viewType: tab.viewType,
        subPage: tab.subPage,
        createdAt: now,
        lastAccessed: now,
        userId: undefined,
      };

      setTabs((prev) => [...prev, newTab]);
      setActiveTabId(newTabId);
      setActiveViewState(duplicatedViewState);

      return newTabId;
    },
    [tabs, createDefaultViewState],
  );

  /**
   * Close a tab
   * When closing the active tab, switches to the nearest tab to the left, or right if none exists
   */
  const closeTab = useCallback(
    (tabId: string) => {
      // Step 1: Find the tab being closed and check if it's the active tab
      const closingTabIndex = tabs.findIndex((t) => t.id === tabId);
      const isClosingActiveTab = activeTabId === tabId;

      // Step 2: Filter out the closed tab
      const filtered = tabs.filter((t) => t.id !== tabId);
      setTabs(filtered);

      // Step 3: Handle active tab closing - switch to nearest tab
      if (isClosingActiveTab) {
        if (filtered.length > 0) {
          // Switch to nearest tab: left if available, otherwise right
          let newActiveTabId: string;
          if (closingTabIndex > 0) {
            // Tab to the left exists
            newActiveTabId = tabs[closingTabIndex - 1].id;
          } else {
            // No tab to the left, use the tab at index 0 (which was at index 1 before filtering)
            newActiveTabId = filtered[0].id;
          }
          setActiveTabId(newActiveTabId);

          // Step 4: Navigate to the new active tab's URL
          const newActiveTab = filtered.find((t) => t.id === newActiveTabId);
          if (newActiveTab) {
            const viewId = newActiveTab.viewId || newActiveTab.id;
            navigate(`/app/t/${tenant}/${viewId}`, { replace: true });
          }
        } else {
          // No tabs left, clear active tab and navigate to base
          setActiveTabId(null);
          navigate(`/app/t/${tenant}`, { replace: true });
        }
      }
      // If closing non-active tab, activeTabId stays the same (no action needed)

      // Step 5: Remove ViewState from storage (use viewId if saved view)
      const closingTab = tabs.find((t) => t.id === tabId);
      const viewId = closingTab?.viewId;
      removeViewState(tabId, viewId).catch((error) => {
        console.error('Failed to remove ViewState:', error);
      });
    },
    [activeTabId, tabs, tenant, navigate],
  );

  /**
   * Close all tabs at once
   * Clears all tabs, active tab, navigates to base, and removes all ViewStates
   */
  const closeAllTabs = useCallback(() => {
    // Store tab IDs and viewIds before clearing for cleanup
    const tabsToCleanup = [...tabs];

    // Clear all tabs, active tab, and collection state
    setTabs([]);
    setActiveTabId(null);
    setActiveCollectionId(null);
    setViewCollectionState({
      name: '',
      description: '',
      viewsIds: [],
      views: [],
      isDirty: false,
    });

    // Navigate to base route
    navigate(`/app/t/${tenant}`, { replace: true });

    // Remove viewCollection and ViewStates from storage
    try {
      sessionStorage.removeItem(`viewCollection_${tenant}`);
    } catch {
      console.error('Failed to remove viewCollection from storage');
    }
    tabsToCleanup.forEach((tab) => {
      const viewId = tab.viewId;
      removeViewState(tab.id, viewId).catch((error) => {
        console.error('Failed to remove ViewState:', error);
      });
    });
  }, [tabs, tenant, navigate]);

  /**
   * Close all tabs except the one with keepTabId.
   * Updates persistence (tabs-<tenant> = [current tab], tab-<id> = only current) and local state in one go
   * so we don't lose context by navigating (as with looping closeTab).
   */
  const closeOtherTabs = useCallback(
    async (keepTabId: string) => {
      const currentTab = tabs.find((t) => t.id === keepTabId);
      if (!currentTab) return;

      const newTabs = [currentTab];
      setTabs(newTabs);
      setActiveTabId(currentTab.id);
      saveTabsMetadata(tenant, newTabs);
      saveActiveTabId(tenant, currentTab.id);

      try {
        await removeAllViewStatesExcept(currentTab.id, currentTab.viewId);
      } catch (error) {
        console.error('Failed to remove other ViewStates:', error);
      }

      const viewId = currentTab.viewId || currentTab.id;
      navigate(`/app/t/${tenant}/${viewId}`, { replace: true });
    },
    [tabs, tenant, navigate],
  );

  /**
   * Open tab in new browser window
   */
  const openTabInNewWindow = useCallback(
    (tabId: string) => {
      //TOD: works only for saved views, need to handle unsaved views as well
      const url = `/app/t/${tenant}/${tabId}?newSession=true`;
      window.open(url, '_blank', 'noopener');
    },
    [tenant],
  );

  /**
   * Get ViewState for a given tab id
   * Always returns ViewState (creates default if not found)
   */
  const getViewState = useCallback(
    async (id: string): Promise<ViewState> => {
      // Find the tab to get its viewId if it's a saved view
      const tab = tabs.find((t) => t.id === id);
      if (!tab) {
        // Tab not found, return default ViewState
        const defaultTab: Tab = {
          id,
          type: 'VIEWS',
          name: '',
          saved: false,
          viewType: ViewType.MONITOR,
          createdAt: Date.now(),
          lastAccessed: Date.now(),
        };
        return createDefaultViewState(defaultTab);
      }

      const viewId = tab.viewId;
      const viewState = await restoreViewState(id, viewId);
      if (viewState) {
        return viewState;
      }

      // If restore fails, return default ViewState for the tab
      return createDefaultViewState(tab);
    },
    [tabs, createDefaultViewState],
  );

  // TAB — Tab metadata (update tab to backend, update metadata, replace tab id with viewId)
  /**
   * Update tab - saves the currentViewState of the passed tabId to backend
   * Applies only to saved views
   */
  const updateTab = useCallback(
    async (tabId: string): Promise<void> => {
      const tab = tabs.find((t) => t.id === tabId);
      if (!tab || !tab.saved || !tab.viewId) {
        throw new Error('Tab is not a saved view');
      }

      // Get current ViewState
      const viewState = await getViewState(tabId);

      // TODO: Call API to update view
      // await updateView({ view_id: tab.viewId, ...viewState });

      // For now, just update local state
      await storeViewState(tabId, viewState);

      // Update tab metadata: update lastAccessed timestamp
      setTabs((prev) =>
        prev.map((t) =>
          t.id === tabId
            ? {
                ...t,
                lastAccessed: Date.now(),
              }
            : t,
        ),
      );

      // Update activeViewState if this is the active tab
      if (tabId === activeTabId) {
        setActiveViewState(viewState);
      }
    },
    [tabs, getViewState, activeTabId],
  );

  /**
   * Update tab metadata (name, viewType, etc.).
   * For VIEWS tabs, when viewType is updated we also persist selectedMode in viewState (tab-<tab-id>) so they stay in sync.
   * Uses latest known state: active tab uses activeViewState, others use getViewState, to avoid stale overwrites.
   */
  const updateTabMetadata = useCallback(
    (tabId: string, updates: Partial<Omit<Tab, 'id' | 'createdAt' | 'lastAccessed'>>) => {
      const viewType = updates.viewType;
      const markDirty = viewType !== undefined;
      setTabs((prev) =>
        prev.map((t) =>
          t.id === tabId ? { ...t, ...updates, ...(markDirty ? { isDirty: true } : {}) } : t,
        ),
      );
      if (viewType !== undefined) {
        if (tabId === activeTabId) {
          // For active tab, also update activeViewState.selectedMode and mark dirty
          setActiveViewState((prev) => {
            const updated = { ...prev, selectedMode: viewType, isDirty: true };
            storeViewState(tabId, updated).catch((err) =>
              console.error('Failed to sync selectedMode to viewState:', err),
            );
            return updated;
          });
          return;
        }
        getViewState(tabId)
          .then((vs) => storeViewState(tabId, { ...vs, selectedMode: viewType, isDirty: true }))
          .catch((err) => console.error('Failed to sync selectedMode to viewState:', err));
      }
    },
    [getViewState, activeTabId],
  );

  /**
   * Replace an unsaved tab's id with the new viewId after saving.
   * Removes old tab-<uuid> from persistence, stores viewState under tab-<viewId>,
   * and updates the tab's id to viewId so id === viewId for saved tabs.
   */
  const replaceTabIdWithViewId = useCallback(
    async (
      oldTabId: string,
      viewId: string,
      metadataUpdates: Partial<Omit<Tab, 'id' | 'createdAt' | 'lastAccessed'>>,
      updatedViewState: ViewState,
    ) => {
      await removeViewState(oldTabId, viewId);
      await storeViewState(viewId, updatedViewState);
      setTabs((prev) =>
        prev.map((t) => (t.id === oldTabId ? { ...t, id: viewId, viewId, ...metadataUpdates } : t)),
      );
      if (activeTabId === oldTabId) {
        setActiveTabId(viewId);
        setActiveViewState(updatedViewState);
      }
    },
    [activeTabId],
  );

  /**
   * Normalize TabbedView to ensure direct indexing is consistent
   */
  const normalizeTabbedView = useCallback((view: TabbedView): TabbedView => {
    // Extract direct indexed views
    const monitorView = view[ViewType.MONITOR];
    const analyzeView = view[ViewType.ANALYZE];
    const investigateView = view[ViewType.INVESTIGATE];

    // Return normalized view with defaults for VIEWS types if missing
    return {
      // VIEWS tab types - ensure defaults if missing
      [ViewType.MONITOR]: monitorView || {
        severityFilter: 0,
        showLimits: true,
        selectedFolders: [],
        selectedTags: [],
      },
      [ViewType.ANALYZE]: analyzeView || {
        selectedTags: [],
        showLimits: true,
        showLabeledEvents: true,
        pinnedTags: [],
        hiddenTags: [],
        selectedFolders: [],
        isTrendSearch: false,
        selectedModelPreview: null,
      },
      [ViewType.INVESTIGATE]: investigateView || {
        selectedFolders: [],
        selectedTags: {},
        showAnomalies: true,
        organize: 'overlays' as const,
        groupByUom: false,
        showLimits: true,
        highlightedTag: null,
        lockedTag: null,
      },
      // Preserve other view types
      [ViewType.ALERT_BOARD]: view[ViewType.ALERT_BOARD],
      [ViewType.ALERT_MANAGER]: view[ViewType.ALERT_MANAGER],
      [ViewType.LIMIT_MANAGER]: view[ViewType.LIMIT_MANAGER],
      [ViewType.MANAGE_MONITOR]: view[ViewType.MANAGE_MONITOR],
      [ViewType.MANAGE_FORMULAS]: view[ViewType.MANAGE_FORMULAS],
      [ViewType.MANAGE_INHERITANCE]: view[ViewType.MANAGE_INHERITANCE],
      [ViewType.MANAGE_ALERTS]: view[ViewType.MANAGE_ALERTS],
      [ViewType.MANAGE_USER_GROUPS]: view[ViewType.MANAGE_USER_GROUPS],
      [ViewType.MANAGE_WEBHOOKS]: view[ViewType.MANAGE_WEBHOOKS],
    };
  }, []);

  /**
   * Update active tab's ViewState
   * Optimistically updates UI first, then persists asynchronously without blocking
   * Automatically marks viewState as dirty and updates tab's lastAccessed timestamp
   * Normalizes view to ensure both direct indexing and tabbedView are in sync
   *
   * Race condition handling: Since state is updated optimistically first,
   * rapid successive calls will update the UI immediately with the latest state.
   * Storage operations may complete out of order, but the UI always reflects
   * the most recent update, which is the desired behavior for optimistic updates.
   */
  const updateActiveTabViewState = useCallback(
    async (viewState: ViewState) => {
      if (!activeTabId) {
        throw new Error('No active tab to update');
      }

      // Resolve which tab this viewState belongs to (avoids race: apply viewState only to its tab,
      // not to whatever tab is active when a delayed update runs after user switched tabs)
      const viewId = viewState.viewId ?? viewState.view_id;
      const targetTabId = viewId
        ? (() => {
            const tab = tabs.find((t) => t.id === viewId || t.viewId === viewId);
            return tab?.id ?? viewId;
          })()
        : activeTabId;

      if (!targetTabId) return;

      // Normalize view to ensure consistency
      const normalizedView = normalizeTabbedView(viewState.view);

      // Preserve isDirty from caller, or keep current when not provided (only if same tab)
      const updatedViewState: ViewState = {
        ...viewState,
        view: normalizedView,
        isDirty:
          viewState.isDirty !== undefined
            ? viewState.isDirty
            : targetTabId === activeTabId
            ? activeViewState.isDirty
            : undefined,
      };

      if (targetTabId === activeTabId) {
        setActiveViewState(updatedViewState);
      }

      setTabs((prev) =>
        prev.map((t) => {
          if (t.id !== targetTabId) return t;
          const viewType = updatedViewState.selectedMode ?? t.viewType;
          const isTrendSearch =
            t.type === 'VIEWS' && viewType === ViewType.ANALYZE
              ? Boolean(updatedViewState.view[ViewType.ANALYZE]?.isTrendSearch)
              : undefined;
          return {
            ...t,
            lastAccessed: Date.now(),
            isDirty: updatedViewState.isDirty,
            ...(viewState.name !== undefined && { name: viewState.name }),
            ...(viewState.description !== undefined && { description: viewState.description }),
            ...(t.type === 'VIEWS' &&
              updatedViewState.selectedMode !== undefined &&
              (t.viewType === undefined ||
                updatedViewState.selectedMode !== ViewType.MONITOR ||
                t.viewType === ViewType.MONITOR) && {
                viewType: updatedViewState.selectedMode,
              }),
            isTrendSearch,
          };
        }),
      );

      // Persist to the tab that owns this viewState
      storeViewState(targetTabId, updatedViewState).catch((error) => {
        console.error('Failed to persist viewState:', error);
      });
    },
    [activeTabId, activeViewState, normalizeTabbedView, tabs],
  );

  /**
   * Mark the active tab's viewState as dirty. Call this when user actions should flag unsaved changes.
   */
  const makeDirty = useCallback(async (): Promise<void> => {
    if (!activeTabId || !activeViewState) return;
    await updateActiveTabViewState({ ...activeViewState, isDirty: true });
  }, [activeTabId, activeViewState, updateActiveTabViewState]);

  /**
   * Clear trend search data from ANALYZE view
   */
  const clearTrendSearchData = useCallback(async (): Promise<void> => {
    if (!activeTabId) {
      throw new Error('No active tab to update');
    }

    const currentViewState = await getViewState(activeTabId);
    const analyzeView = currentViewState.view[ViewType.ANALYZE];

    // Create updated ANALYZE view without trendSearchData
    const updatedAnalyzeView = {
      ...analyzeView,
      isTrendSearch: false,
    };
    delete updatedAnalyzeView.trendSearchData;

    // Create updated ViewState with cleared trend search data
    const updatedViewState: ViewState = {
      ...currentViewState,
      view: {
        ...currentViewState.view,
        [ViewType.ANALYZE]: updatedAnalyzeView,
      },
    };

    // Store the updated viewState
    await storeViewState(activeTabId, updatedViewState);

    // Update activeViewState
    setActiveViewState(updatedViewState);

    // Update tab metadata: lastAccessed and isTrendSearch
    setTabs((prev) =>
      prev.map((t) =>
        t.id === activeTabId
          ? {
              ...t,
              lastAccessed: Date.now(),
              isTrendSearch: false,
            }
          : t,
      ),
    );
  }, [activeTabId, getViewState]);

  /**
   * Set trend search data in ANALYZE view
   */
  const setTrendSearchData = useCallback(
    async (trendSearchData: TrendSearchData, isDirty?: boolean): Promise<void> => {
      if (!activeTabId) {
        throw new Error('No active tab to update');
      }

      const currentViewState = await getViewState(activeTabId);
      const analyzeView = currentViewState.view[ViewType.ANALYZE];

      // Create updated ANALYZE view with trendSearchData
      const updatedAnalyzeView = {
        ...analyzeView,
        trendSearchData,
        isTrendSearch: true,
      };

      // Create updated ViewState with trend search data
      const updatedViewState: ViewState = {
        ...currentViewState,
        view: {
          ...currentViewState.view,
          [ViewType.ANALYZE]: updatedAnalyzeView,
        },
        isDirty: isDirty ?? false,
      };

      // Store the updated viewState
      await storeViewState(activeTabId, updatedViewState);

      // Update activeViewState
      setActiveViewState(updatedViewState);

      // Update tab metadata: lastAccessed and isTrendSearch
      setTabs((prev) =>
        prev.map((t) =>
          t.id === activeTabId
            ? {
                ...t,
                lastAccessed: Date.now(),
                isTrendSearch: true,
              }
            : t,
        ),
      );
    },
    [activeTabId, getViewState],
  );

  /**
   * Delete tab - calls the delete endpoint passing the viewId
   * Applies only to saved view
   */
  const deleteTab = useCallback(
    async (viewId: string): Promise<void> => {
      // Find the tab by viewId
      const tab = tabs.find((t) => t.viewId === viewId);
      if (!tab || !tab.saved) {
        throw new Error('Tab is not a saved view');
      }

      // Only delete if created by current user
      if (tab.userId !== undefined && tab.userId !== userId) {
        // Tab was created by a different user, don't delete
        return;
      }

      // TODO: Call API to delete view
      // await deleteView({ view_id: viewId, user_id: userId });

      // Remove tab from tabs array
      setTabs((prev) => prev.filter((t) => t.id !== tab.id));

      // If deleting active tab, switch to another tab or clear activeTabId
      if (activeTabId === tab.id) {
        const remainingTabs = tabs.filter((t) => t.id !== tab.id);
        if (remainingTabs.length > 0) {
          // Switch to the tab that was to the left, or the first remaining tab
          const closedTabIndex = tabs.findIndex((t) => t.id === tab.id);
          const newActiveTabIndex = Math.max(0, closedTabIndex - 1);
          setActiveTabId(remainingTabs[newActiveTabIndex].id);
        } else {
          setActiveTabId(null);
        }
      }

      // Remove persisted view state for this tab
      removeViewState(tab.id, tab.viewId).catch((error) => {
        console.error(`Failed to remove view state for deleted tab ${tab.id}:`, error);
      });
    },
    [tabs, activeTabId, userId],
  );

  // PERSISTENCE — View state (load): restore active tab's view state from storage when tab changes
  useEffect(() => {
    const loadActiveViewState = async () => {
      if (!activeTabId) {
        setActiveViewState(getDefaultViewState());
        return;
      }

      try {
        const viewState = await getViewState(activeTabId);
        const activeTab = tabs.find((t) => t.id === activeTabId);
        if (
          activeTab?.type === 'VIEWS' &&
          activeTab.viewType != null &&
          viewState.selectedMode !== activeTab.viewType
        ) {
          const synced = { ...viewState, selectedMode: activeTab.viewType };
          setActiveViewState(synced);
          storeViewState(activeTabId, synced).catch((err) =>
            console.error('Failed to sync selectedMode from tab meta:', err),
          );
        } else {
          setActiveViewState(viewState);
        }
      } catch (error) {
        console.error('Failed to load active viewState:', error);
        setActiveViewState(getDefaultViewState());
      }
    };

    loadActiveViewState();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on tab switch; tabs read from closure is current at switch time; adding tabs would re-run on every metadata update
  }, [activeTabId, getViewState, getDefaultViewState]);

  // Tab meta viewType (tabs-<tenant>) is source of truth; sync to viewState.selectedMode (tab-<tab-id>) when loading.

  /**
   * Open in new tab - create a fresh new tab with meta and a viewState
   */
  const openInNewTab = useCallback(
    async (viewState: ViewState): Promise<string> => {
      // Generate new tab ID
      const newTabId = uuidv4();
      const now = Date.now();

      // Determine tab type and name from ViewState
      const selectedMode = viewState.selectedMode;
      const tabType: TabType = 'VIEWS'; // ViewState is always for VIEWS tabs
      const viewType = selectedMode;

      // Ephemeral tabs always have empty name until the user saves the view and names it
      const tabName = '';
      const isTrendSearch =
        viewType === ViewType.ANALYZE && Boolean(viewState.view[ViewType.ANALYZE]?.isTrendSearch);

      // Create tab metadata
      const newTab: Tab = {
        id: newTabId,
        type: tabType,
        name: tabName,
        saved: false, // New tabs from ViewState are always unsaved
        viewId: undefined,
        viewType: viewType,
        isTrendSearch: viewType === ViewType.ANALYZE ? isTrendSearch : undefined,
        createdAt: now,
        lastAccessed: now,
        userId: undefined,
      };

      // Add tab metadata to tabs array
      setTabs((prev) => [...prev, newTab]);

      // Set activeTabId to the newly created tab
      setActiveTabId(newTabId);

      // Update ViewState with new tabId; keep name empty for ephemeral tabs until user saves
      const newViewState: ViewState = {
        ...viewState,
        viewId: newTabId, // Use tabId as viewId for new unsaved tabs
        view_id: newTabId,
        name: '',
      };

      setActiveViewState(newViewState);

      await storeViewState(newTabId, newViewState);

      return newTabId;
    },
    [setTabs, setActiveTabId],
  );

  /**
   * Switch active tab's type
   * Preserves existing ViewState and initializes defaults for new tabType if needed
   * Makes saved views dirty when switching
   */
  const switchTabType = useCallback(
    async (options: CreateTabOptions): Promise<void> => {
      if (!activeTabId) {
        // No active tab, create new one
        await createTabInternal(options);
        return;
      }

      const activeTab = tabs.find((t) => t.id === activeTabId);
      if (!activeTab) {
        // Active tab not found, create new one
        await createTabInternal(options);
        return;
      }

      // Get current ViewState
      const currentViewState = await getViewState(activeTabId);

      const newTabName = options.name ?? '';

      // Preserve existing view state - TabbedView contains all view types
      // Get default ViewState to ensure all defaults exist
      const tempTab: Tab = {
        id: activeTabId,
        type: options.type,
        name: newTabName,
        saved: activeTab.saved,
        viewId: activeTab.viewId,
        viewType: options.viewType,
        subPage: options.subPage,
        createdAt: activeTab.createdAt,
        lastAccessed: Date.now(),
        userId: activeTab.userId,
      };
      const defaultViewState = createDefaultViewState(tempTab);

      // Merge: preserve existing state, initialize defaults for any missing view types
      const updatedView: TabbedView = {
        // VIEWS tab types - preserve existing or use defaults
        [ViewType.MONITOR]:
          currentViewState.view[ViewType.MONITOR] || defaultViewState.view[ViewType.MONITOR],
        [ViewType.ANALYZE]:
          currentViewState.view[ViewType.ANALYZE] || defaultViewState.view[ViewType.ANALYZE],
        [ViewType.INVESTIGATE]:
          currentViewState.view[ViewType.INVESTIGATE] ||
          defaultViewState.view[ViewType.INVESTIGATE],
        // Preserve existing other view types or use defaults
        [ViewType.ALERT_BOARD]:
          currentViewState.view[ViewType.ALERT_BOARD] ||
          defaultViewState.view[ViewType.ALERT_BOARD],
        [ViewType.ALERT_MANAGER]:
          currentViewState.view[ViewType.ALERT_MANAGER] ||
          defaultViewState.view[ViewType.ALERT_MANAGER],
        [ViewType.LIMIT_MANAGER]:
          currentViewState.view[ViewType.LIMIT_MANAGER] ||
          defaultViewState.view[ViewType.LIMIT_MANAGER],
        [ViewType.MANAGE_MONITOR]:
          currentViewState.view[ViewType.MANAGE_MONITOR] ||
          defaultViewState.view[ViewType.MANAGE_MONITOR],
        [ViewType.MANAGE_FORMULAS]:
          currentViewState.view[ViewType.MANAGE_FORMULAS] ||
          defaultViewState.view[ViewType.MANAGE_FORMULAS],
        [ViewType.MANAGE_INHERITANCE]:
          currentViewState.view[ViewType.MANAGE_INHERITANCE] ||
          defaultViewState.view[ViewType.MANAGE_INHERITANCE],
        [ViewType.MANAGE_ALERTS]:
          currentViewState.view[ViewType.MANAGE_ALERTS] ||
          defaultViewState.view[ViewType.MANAGE_ALERTS],
        [ViewType.MANAGE_USER_GROUPS]:
          currentViewState.view[ViewType.MANAGE_USER_GROUPS] ||
          defaultViewState.view[ViewType.MANAGE_USER_GROUPS],
        [ViewType.MANAGE_WEBHOOKS]:
          currentViewState.view[ViewType.MANAGE_WEBHOOKS] ||
          defaultViewState.view[ViewType.MANAGE_WEBHOOKS],
      };

      // Determine selectedMode based on new type
      let newSelectedMode = currentViewState.selectedMode;
      if (options.type === 'VIEWS' && options.viewType) {
        newSelectedMode = options.viewType;
      } else if (options.type !== 'VIEWS') {
        // For non-VIEWS tabs, selectedMode is not relevant but keep it for backward compatibility
        newSelectedMode = ViewType.MONITOR;
      }

      // Update ViewState with new tabType/viewType/subPage
      const updatedViewState: ViewState = {
        ...currentViewState,
        view: updatedView,
        tabType: options.type,
        subPage: options.subPage,
        selectedMode: newSelectedMode,
        isDirty: currentViewState.isDirty,
      };

      // Store updated ViewState
      await storeViewState(activeTabId, updatedViewState);
      setActiveViewState(updatedViewState);

      const isTrendSearch =
        options.type === 'VIEWS' && options.viewType === ViewType.ANALYZE
          ? Boolean(updatedViewState.view[ViewType.ANALYZE]?.isTrendSearch)
          : undefined;

      // Update tab metadata
      updateTabMetadata(activeTabId, {
        type: options.type,
        viewType: options.viewType,
        subPage: options.subPage,
        saved: false, // Mark as unsaved if it was saved (since we switched type)
        name: activeTab.saved ? activeTab.name : newTabName, // Preserve name if saved, otherwise use new name
        isTrendSearch,
      });
    },
    [
      activeTabId,
      tabs,
      getViewState,
      createDefaultViewState,
      createTabInternal,
      updateTabMetadata,
      setActiveViewState,
    ],
  );

  /**
   * Revert tab to original saved ViewState
   * Fetches the original ViewState from API and restores it
   */
  const revertTab = useCallback(
    async (tabId: string): Promise<void> => {
      const tab = tabs.find((t) => t.id === tabId);
      if (!tab || !tab.saved || !tab.viewId) {
        // Can only revert saved views
        return;
      }

      try {
        // Fetch original ViewState from API
        const persistView = await getView(tab.viewId);

        // Get current ViewState to extract ensembleFamily if needed
        const currentViewState = await getViewState(tabId);
        const ensembleFamily = currentViewState.selectedEnsemble || null;

        // Transform to ViewState (includes tabType from PersistView)
        const originalViewState = transformToViewState(
          persistView,
          ensembleFamily,
          tenant,
          tab.viewId,
        );

        // Update tab metadata (name, description, isDirty, tabType, etc.) from reverted viewState
        setTabs((prev) =>
          prev.map((t) =>
            t.id === tabId
              ? {
                  ...t,
                  name: originalViewState.name || t.name,
                  description: originalViewState.description || t.description,
                  isDirty: false,
                  type: originalViewState.tabType ? (originalViewState.tabType as TabType) : t.type,
                  subPage: originalViewState.subPage || t.subPage,
                }
              : t,
          ),
        );

        // Store the reverted ViewState
        await storeViewState(tabId, originalViewState);

        // Update activeViewState if this is the active tab
        if (tabId === activeTabId) {
          setActiveViewState(originalViewState);
        }
      } catch (error) {
        console.error('Failed to revert tab:', error);
        throw error;
      }
    },
    [tabs, tenant, getViewState, activeTabId],
  );

  /**
   * Handle tenant switch (clear all tabs, local viewState, and persisted viewState)
   */
  const handleTenantSwitch = useCallback(() => {
    // Clear local viewState (including selectedEnsemble)
    setActiveViewState(getDefaultViewState());

    // Clear all tabs
    setTabs([]);
    setActiveTabId(null);
    setActiveCollectionId(null);

    // Clear viewCollection
    setViewCollectionState({
      name: '',
      description: '',
      viewsIds: [],
      views: [],
      isDirty: false,
    });

    // Clear sessionStorage for old tenant using new storage functions
    clearTabMetadata(tenant);
    try {
      sessionStorage.removeItem(`viewCollection_${tenant}`);
    } catch {
      // Ignore errors
    }

    // Clear persisted viewState (tab-<id>) for this tenant so selectedEnsemble etc. are not reused
    clearTenantViewStates(tenant).catch(() => {
      // Ignore errors
    });
  }, [tenant, getDefaultViewState]);

  /**
   * Persist tab state (ViewState)
   * Uses viewId for saved views, tabId for unsaved tabs
   */
  const persistTabState = useCallback(async (tabId: string, viewState: ViewState) => {
    await storeViewState(tabId, viewState);
  }, []);

  // ============================================================================
  // COLLECTION — View collection (set, load, close, get saved view ids)
  // ============================================================================

  /**
   * Set viewCollection state
   * Supports both direct updates and functional updates
   */
  const setViewCollection = useCallback(
    (updates: Partial<ViewCollection> | ((prev: ViewCollection) => ViewCollection)): void => {
      if (typeof updates === 'function') {
        setViewCollectionState(updates);
      } else {
        setViewCollectionState((prev) => ({ ...prev, ...updates }));
      }
    },
    [],
  );

  /**
   * Get saved view IDs from current tabs
   * Returns array of viewIds from tabs that are saved
   */
  const getSavedViewIds = useCallback((): string[] => {
    return tabs
      .filter((tab): tab is Tab & { viewId: string } => tab.saved && !!tab.viewId)
      .map((tab) => tab.viewId);
  }, [tabs]);

  /**
   * Load a collection
   * Closes all existing tabs, opens all views from the collection, and sets collection state
   * Loads everything instantly by fetching all views in parallel and creating all tabs at once
   */
  const loadCollection = useCallback(
    async (collection: PersistCollection): Promise<void> => {
      if (!collection.views || collection.views.length === 0) {
        return;
      }

      const collectionId = collection.collection_id;

      // Set active collection ID immediately (shows CollectionTab instantly)
      if (collectionId) {
        setActiveCollectionId(collectionId);
        saveActiveCollectionId(tenant, collectionId);
      }

      // Close all existing tabs in one go (avoid N setTabs/setActiveTabId and effect thrashing)
      const tabsToClose = [...tabs];
      setTabs([]);
      setActiveTabId(null);
      setActiveViewState(getDefaultViewState());
      saveActiveTabId(tenant, null);
      navigate(`/app/t/${tenant}`, { replace: true });
      // Clean up persisted view state for closed tabs (fire-and-forget)
      tabsToClose.forEach((tab) => {
        const vid = tab.viewId;
        removeViewState(tab.id, vid).catch((error) => {
          console.error(`Failed to remove view state for tab ${tab.id}:`, error);
        });
      });

      // Extract view IDs from collection
      const viewIds: string[] = collection.views
        .map((view) => view.view_id)
        .filter((id): id is string => id !== undefined && id !== 'default');

      if (viewIds.length === 0) {
        // Set collection state even if no views
        setViewCollection({
          viewsIds: [],
          name: collection.collection_name || '',
          description: collection.description || '',
          isDirty: false,
          collectionId: collectionId,
          views: collection.views,
        });
        return;
      }

      // Fetch all views in parallel
      const viewPromises = viewIds.map(async (viewId) => {
        try {
          const tabId = viewId;
          let savedViewState = await restoreViewState(tabId, viewId);

          // If not in storage, fetch from backend
          if (!savedViewState) {
            try {
              const persistView = await getView(viewId);
              savedViewState = transformToViewState(
                persistView,
                null, // ensembleFamily - will be set later if needed
                tenant,
                viewId,
              );
            } catch (error) {
              console.error(`Failed to fetch view ${viewId}:`, error);
              return null;
            }
          }

          // Determine tab type and view type from ViewState or PersistView
          let tabType: TabType = savedViewState?.tabType || 'VIEWS';
          let viewType: ViewType | undefined = savedViewState?.selectedMode;
          let subPage: SubPage | undefined = savedViewState?.subPage;

          // If ViewState doesn't have tabType, try to get from collection view
          if (!savedViewState?.tabType && collection.views?.length) {
            const persistView = collection.views.find((v) => v.view_id === viewId);
            if (persistView) {
              tabType = persistView.view_type || 'VIEWS';
              if (persistView.view?.type) {
                viewType = persistView.view.type as ViewType;
              }
              subPage = persistView.sub_page;
            }
          }

          const now = Date.now();
          const tabName: string =
            savedViewState?.name ||
            (tabType === 'VIEWS'
              ? (() => {
                  const viewTypeNames: Partial<Record<ViewType, string>> = {
                    [ViewType.MONITOR]: 'Monitor',
                    [ViewType.ANALYZE]: 'Analyze',
                    [ViewType.INVESTIGATE]: 'Investigate',
                  };
                  return viewTypeNames[viewType || ViewType.MONITOR] || 'Monitor';
                })()
              : tabType === 'ALERT_BOARD'
              ? 'Alert Board'
              : tabType === 'ALERT_MANAGER'
              ? 'Alert Manager'
              : tabType === 'LIMIT_MANAGER'
              ? 'Limit Manager'
              : tabType === 'MANAGE'
              ? (() => {
                  const subPageNames: Record<SubPage, string> = {
                    monitors: 'Manage Monitors',
                    formulas: 'Manage Formulas',
                    inheritance: 'Manage Inheritance',
                    alerts: 'Manage Alerts',
                    user_groups: 'Manage Users & Groups',
                    webhooks: 'Manage Webhooks',
                  };
                  return subPageNames[subPage || 'monitors'];
                })()
              : 'Tab');

          const newTab: Tab = {
            id: tabId,
            type: tabType,
            name: tabName,
            description: savedViewState?.description,
            saved: true,
            viewId: viewId,
            viewType: viewType,
            subPage: subPage,
            createdAt: now,
            lastAccessed: now,
            userId: userId,
          };

          // Use saved ViewState or create default
          const viewStateToStore = savedViewState || createDefaultViewState(newTab);
          viewStateToStore.viewId = viewId;
          viewStateToStore.view_id = viewId;

          return {
            tab: newTab,
            viewState: viewStateToStore,
          };
        } catch (error) {
          console.error(`Failed to process view ${viewId}:`, error);
          return null;
        }
      });

      // Wait for all views to be fetched and processed
      const tabDataArray = await Promise.all(viewPromises);
      const validTabData = tabDataArray.filter(
        (data): data is { tab: Tab; viewState: ViewState } => data !== null,
      );

      if (validTabData.length === 0) {
        // No valid tabs created, set collection state and return
        setViewCollection({
          viewsIds: viewIds,
          name: collection.collection_name || '',
          description: collection.description || '',
          isDirty: false,
          collectionId: collectionId,
          views: collection.views,
        });
        return;
      }

      // Store all ViewStates in parallel
      await Promise.all(
        validTabData.map(({ tab, viewState }) => storeViewState(tab.id, viewState)),
      );

      // Create all tabs at once
      const newTabs = validTabData.map(({ tab }) => tab);
      setTabs(newTabs);

      // Set the first tab as active
      const firstTabId = newTabs[0].id;
      setActiveTabId(firstTabId);
      const firstViewState = validTabData[0].viewState;
      setActiveViewState(firstViewState);

      // Set collection state
      setViewCollection({
        viewsIds: viewIds,
        name: collection.collection_name || '',
        description: collection.description || '',
        isDirty: false,
        collectionId: collectionId,
        views: collection.views,
      });
    },
    [
      tabs,
      setViewCollection,
      tenant,
      userId,
      createDefaultViewState,
      getDefaultViewState,
      navigate,
      setActiveTabId,
      setActiveViewState,
    ],
  );

  /**
   * Close the current collection
   * Clears collection state and activeCollectionId
   */
  const closeCollection = useCallback(() => {
    setViewCollection({
      name: '',
      description: '',
      viewsIds: [],
      views: [],
      isDirty: false,
    });
    setActiveCollectionId(null);
    saveActiveCollectionId(tenant, null);
  }, [setViewCollection, tenant]);

  /**
   * Restore tab state (ViewState)
   * Tries viewId first (for saved views), then tabId (for unsaved tabs)
   */
  // const restoreTabState = useCallback(
  //   async (tabId: string): Promise<ViewState | null> => {
  //     // Find the tab to get its viewId if it's a saved view
  //     const tab = tabs.find((t) => t.id === tabId);
  //     const viewId = tab?.viewId;
  //     return restoreViewState(tabId, viewId);
  //   },
  //   [tabs],
  // );

  /**
   * Generate query key with active tab ID for cache isolation
   * This ensures that queries are scoped to the active tab, preventing cache collisions
   */
  const getQueryKeyWithViewId = useCallback(
    (label: string) => `${label}-${activeTabId || 'default'}`,
    [activeTabId],
  );

  /**
   * Record current timeSelection to history before making changes
   * This enables undo functionality for timeSelection changes
   * Only works for VIEWS tab type where Monitor, Assess, and Investigate share timeSelection
   */
  const recordTimelineHistory = useCallback(async () => {
    if (!activeTabId || !activeViewState) return;

    // Only record history for VIEWS tab type (Monitor, Assess, Investigate share timeSelection)
    // Other tab types (ALERT_BOARD, ALERT_MANAGER, etc.) have their own timeSelection handling
    const activeTab = tabs.find((tab) => tab.id === activeTabId);
    if (activeTab?.type !== 'VIEWS') return;

    await updateActiveTabViewState({
      ...activeViewState,
      timeSelectionHistory: [
        ...activeViewState.timeSelectionHistory,
        activeViewState.timeSelection,
      ],
    });
  }, [activeTabId, activeViewState, tabs, updateActiveTabViewState]);

  /**
   * Undo timeSelection to previous state from history
   * Pops the last entry from timeSelectionHistory and restores it
   * Only works for VIEWS tab type where Monitor, Assess, and Investigate share timeSelection
   */
  const handleUndoTimeSelection = useCallback(async () => {
    if (!activeTabId || !activeViewState || !activeViewState.timeSelectionHistory.length) return;

    // Only allow undo for VIEWS tab type
    const activeTab = tabs.find((tab) => tab.id === activeTabId);
    if (activeTab?.type !== 'VIEWS') return;

    const historyCopy = [...activeViewState.timeSelectionHistory];
    const resetTo = historyCopy.pop();
    if (!resetTo) return;

    await updateActiveTabViewState({
      ...activeViewState,
      timeSelection: {
        ...resetTo,
        startTime: dayjs.isDayjs(resetTo.startTime) ? resetTo.startTime : dayjs(resetTo.startTime),
        endTime: dayjs.isDayjs(resetTo.endTime) ? resetTo.endTime : dayjs(resetTo.endTime),
      },
      timeSelectionHistory: historyCopy,
    });
  }, [activeTabId, activeViewState, tabs, updateActiveTabViewState]);

  /**
   * SearchMode – derived from viewState.view[ANALYZE].isTrendSearchEnabled (persists per tab).
   * Distinct from isTrendSearch (which controls Time Search vs Assess); this is the search-mode toggle (banner / drag-to-trend-search).
   */
  const searchMode = activeViewState?.view?.[ViewType.ANALYZE]?.isTrendSearchEnabled ?? false;

  const setSearchMode = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      if (!activeTabId || !activeViewState) return;
      const next = typeof value === 'function' ? value(searchMode) : value;
      const analyzeView = activeViewState.view[ViewType.ANALYZE];
      updateActiveTabViewState({
        ...activeViewState,
        view: {
          ...activeViewState.view,
          [ViewType.ANALYZE]: {
            ...analyzeView,
            isTrendSearchEnabled: next,
            selectedModelPreview: null,
          },
        },
      }).catch(() => undefined);
    },
    [activeTabId, activeViewState, searchMode, updateActiveTabViewState],
  );

  /**
   * Increase chart height for Assess view
   * Cycles through chartHeightIncrements array
   */
  const handleIncrementAssessChartHeight = useCallback(async () => {
    if (!activeTabId || !activeViewState) return;

    const currentHeight =
      activeViewState.config?.[ViewType.ANALYZE]?.chartHeight ?? chartHeightIncrements[0];
    const currentIndex = chartHeightIncrements.indexOf(currentHeight);
    const nextIndex = Math.min(currentIndex + 1, chartHeightIncrements.length - 1);
    const newHeight = chartHeightIncrements[nextIndex];

    await updateActiveTabViewState({
      ...activeViewState,
      config: {
        ...activeViewState.config,
        [ViewType.ANALYZE]: {
          ...activeViewState.config?.[ViewType.ANALYZE],
          chartHeight: newHeight,
        },
      },
    });
  }, [activeTabId, activeViewState, updateActiveTabViewState]);

  /**
   * Decrease chart height for Assess view
   * Cycles through chartHeightIncrements array
   */
  const handleDecrementAssessChartHeight = useCallback(async () => {
    if (!activeTabId || !activeViewState) return;

    const currentHeight =
      activeViewState.config?.[ViewType.ANALYZE]?.chartHeight ?? chartHeightIncrements[0];
    const currentIndex = chartHeightIncrements.indexOf(currentHeight);
    const nextIndex = Math.max(currentIndex - 1, 0);
    const newHeight = chartHeightIncrements[nextIndex];

    await updateActiveTabViewState({
      ...activeViewState,
      config: {
        ...activeViewState.config,
        [ViewType.ANALYZE]: {
          ...activeViewState.config?.[ViewType.ANALYZE],
          chartHeight: newHeight,
        },
      },
    });
  }, [activeTabId, activeViewState, updateActiveTabViewState]);

  const value: TabManagerContextType = useMemo(
    () => ({
      tabs,
      activeTabId,
      activeViewState,
      getViewState,
      createTab,
      addNewTab,
      openInNewTab,
      duplicateTab,
      switchTab,
      openSavedView,
      closeTab,
      closeAllTabs,
      closeOtherTabs,
      openTabInNewWindow,
      updateTab,
      updateTabMetadata,
      replaceTabIdWithViewId,
      updateActiveTabViewState,
      makeDirty,
      persistTabState,
      deleteTab,
      revertTab,
      handleTenantSwitch,
      switchTabType,
      clearTrendSearchData,
      setTrendSearchData,
      viewCollection,
      setViewCollection,
      loadCollection,
      closeCollection,
      activeCollectionId,
      getSavedViewIds,
      getQueryKeyWithViewId,
      recordTimelineHistory,
      handleUndoTimeSelection,
      searchMode,
      setSearchMode,
      handleIncrementAssessChartHeight,
      handleDecrementAssessChartHeight,
    }),
    [
      tabs,
      activeTabId,
      activeViewState,
      getViewState,
      createTab,
      addNewTab,
      openInNewTab,
      duplicateTab,
      switchTab,
      openSavedView,
      closeTab,
      closeAllTabs,
      closeOtherTabs,
      openTabInNewWindow,
      updateTab,
      updateTabMetadata,
      replaceTabIdWithViewId,
      updateActiveTabViewState,
      makeDirty,
      persistTabState,
      deleteTab,
      revertTab,
      handleTenantSwitch,
      switchTabType,
      clearTrendSearchData,
      setTrendSearchData,
      viewCollection,
      setViewCollection,
      loadCollection,
      closeCollection,
      activeCollectionId,
      getSavedViewIds,
      getQueryKeyWithViewId,
      recordTimelineHistory,
      handleUndoTimeSelection,
      searchMode,
      setSearchMode,
      handleIncrementAssessChartHeight,
      handleDecrementAssessChartHeight,
    ],
  );

  const displayValue = displayActiveTabId ?? activeTabId;
  return (
    <TabDisplayContext.Provider value={displayValue}>
      <TabManagerContext.Provider value={value}>{children}</TabManagerContext.Provider>
    </TabDisplayContext.Provider>
  );
};

/**
 * Hook to use TabManagerContext
 */
export const useTabManager = (): TabManagerContextType => {
  const context = useContext(TabManagerContext);
  if (!context) {
    throw new Error('useTabManager must be used within TabManagerProvider');
  }
  return context;
};

/**
 * Hook for tab bar only: returns the active tab id to display (optimistically updated on switch).
 * Use this for isActive in tab items so selection highlights immediately without waiting for heavy tree commit.
 */
export const useTabDisplayActiveId = (): string | null => {
  return useContext(TabDisplayContext);
};
